import React, { useEffect } from 'react';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  position?: 'right' | 'left';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

export const Drawer = ({ isOpen, onClose, title, children, footer, position = 'right', size = 'md' }: DrawerProps) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'w-full sm:w-80',
    md: 'w-full sm:w-[580px]',
    lg: 'w-full sm:max-w-2xl',
    xl: 'w-full sm:max-w-4xl',
    full: 'w-full'
  };

  const positionClasses = position === 'right' ? 'right-0' : 'left-0';

  return (
    <div className="fixed inset-0 z-[100] flex">
      <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" onClick={onClose} />
      
      <div className={`fixed inset-y-0 ${positionClasses} ${sizeClasses[size]} bg-white shadow-2xl flex flex-col transition-transform`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex-1 pr-4">{title}</div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors rounded-full p-1 hover:bg-gray-100 cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        
        <div className="flex-1 p-6 overflow-y-auto custom-scrollbar">
          {children}
        </div>
        
        {footer && (
          <div className="border-t border-gray-100 bg-white shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
